import React, { useState, useEffect, useCallback, useRef } from "react";
import ScreenSelection from "./scoreboard/ScreenSelection";
import QuadScreen from "./scoreboard/QuadScreen";
import SingleScreen from "./scoreboard/SingleScreen";
import PreCompetitionScreen from "./scoreboard/PreCompetitionScreen";
import ScoreboardHeader from "./scoreboard/ScoreboardHeader";

const ScoreboardShow = ({ competition_id, routes, t, show_react_header }) => {
  const [screenMode, setScreenMode] = useState('selection'); // 'selection', 'single', 'quad'
  const [competitionData, setCompetitionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPreCompetition, setIsPreCompetition] = useState(false); // Отдельное состояние для overlay
  const initialLoadDone = useRef(false);
  const [screenConfigs, setScreenConfigs] = useState([
    { mode: 'nomination_selection', selectedNomination: null, selectedView: null },
    { mode: 'nomination_selection', selectedNomination: null, selectedView: null },
    { mode: 'nomination_selection', selectedNomination: null, selectedView: null },
    { mode: 'nomination_selection', selectedNomination: null, selectedView: null }
  ]);
  const [singleConfig, setSingleConfig] = useState({
    mode: 'nomination_selection',
    selectedNomination: null,
    selectedView: null
  });

  // Загрузка параметров из URL при загрузке компонента
  useEffect(() => {
    fetchCompetitionData();
    loadFromURL();
  }, []);

  // Обновление данных соревнования каждые 30 секунд
  useEffect(() => {
    const interval = setInterval(() => {
      fetchCompetitionData();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const fetchCompetitionData = async () => {
    try {
      const response = await fetch(`/scoreboard/${competition_id}.json`);
      const data = await response.json();
      setCompetitionData(data);
      
      // Если соревнование еще не началось, показываем PreCompetitionScreen как overlay
      if (data.status === 'future') {
        setIsPreCompetition(true);
      } else if (isPreCompetition && data.status !== 'future') {
        // Если соревнование началось, перезагружаем страницу для применения параметров URL
        window.location.reload();
        return;
      }
      
      setLoading(false);
    } catch (error) {
      console.error("Error fetching competition data:", error);
      setLoading(false);
    }
  };

  const handleCompetitionStart = () => {
    // Перезагружаем страницу при начале соревнования
    window.location.reload();
  };

  // Сохранение параметров в URL при изменении состояния (только после первичной загрузки)
  useEffect(() => {
    if (initialLoadDone.current) {
      updateURL();
    }
  }, [screenMode, screenConfigs, singleConfig]);

  const loadFromURL = () => {
    const params = new URLSearchParams(window.location.search);
    const mode = params.get('mode');
    
    // Не устанавливаем mode если его нет в URL или если он pre-competition
    // pre-competition теперь обрабатывается отдельно через isPreCompetition
    if (mode && mode !== 'pre-competition') {
      setScreenMode(mode);
    }
    
    if (mode === 'quad') {
      const newConfigs = [...screenConfigs];
      for (let i = 0; i < 4; i++) {
        newConfigs[i] = {
          mode: params.get(`screen${i}_mode`) || 'nomination_selection',
          selectedNomination: params.get(`screen${i}_nomination`) || null,
          selectedView: params.get(`screen${i}_view`) || null
        };
      }
      setScreenConfigs(newConfigs);
    } else if (mode === 'single') {
      setSingleConfig({
        mode: params.get('single_mode') || 'nomination_selection',
        selectedNomination: params.get('single_nomination') || null,
        selectedView: params.get('single_view') || null
      });
    }
    
    // После загрузки из URL разрешаем обновление URL
    initialLoadDone.current = true;
  };

  const updateURL = () => {
    const params = new URLSearchParams();
    params.set('mode', screenMode);
    
    if (screenMode === 'quad') {
      screenConfigs.forEach((config, index) => {
        params.set(`screen${index}_mode`, config.mode);
        if (config.selectedNomination) {
          params.set(`screen${index}_nomination`, config.selectedNomination);
        }
        if (config.selectedView) {
          params.set(`screen${index}_view`, config.selectedView);
        }
      });
    } else if (screenMode === 'single') {
      params.set('single_mode', singleConfig.mode);
      if (singleConfig.selectedNomination) {
        params.set('single_nomination', singleConfig.selectedNomination);
      }
      if (singleConfig.selectedView) {
        params.set('single_view', singleConfig.selectedView);
      }
    }
    
    const newURL = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', newURL);
  };

  const handleScreenModeChange = (mode) => {
    setScreenMode(mode);
  };

  const handleQuadConfigChange = useCallback((screenIndex, newConfig) => {
    setScreenConfigs(prev => {
      const newConfigs = [...prev];
      newConfigs[screenIndex] = { ...newConfigs[screenIndex], ...newConfig };
      return newConfigs;
    });
  }, []);

  const handleSingleConfigChange = useCallback((newConfig) => {
    setSingleConfig(prev => ({ ...prev, ...newConfig }));
  }, []);

  const handleSettingsClick = () => {
    setScreenMode('selection');
  };

  const handleSingleSettingsClick = () => {
    // В single режиме просто переводим в режим выбора номинации
    setSingleConfig({
      mode: 'nomination_selection',
      selectedNomination: null,
      selectedView: null
    });
  };

  const handleQuadSettingsClick = (screenIndex) => {
    // В quad режиме переводим только конкретную секцию в режим выбора номинации
    setScreenConfigs(prev => {
      const newConfigs = [...prev];
      newConfigs[screenIndex] = {
        mode: 'nomination_selection',
        selectedNomination: null,
        selectedView: null
      };
      return newConfigs;
    });
  };

  const handleSwitchScreenMode = (newMode) => {
    if (newMode === 'single') {
      // При переключении с quad на single, оставляем текущие настройки на весь экран
      setScreenMode('single');
    } else if (newMode === 'quad') {
      // При переключении с single на quad, заполняем все 4 экрана панелями выбора номинаций
      setScreenMode('quad');
      setScreenConfigs([
        { mode: 'nomination_selection', selectedNomination: null, selectedView: null },
        { mode: 'nomination_selection', selectedNomination: null, selectedView: null },
        { mode: 'nomination_selection', selectedNomination: null, selectedView: null },
        { mode: 'nomination_selection', selectedNomination: null, selectedView: null }
      ]);
    }
  };

  if (loading) {
    return (
      <div className="text-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">{t.loading}</span>
        </div>
      </div>
    );
  }

  // Pre-competition overlay отображается поверх основного контента
  const preCompetitionOverlay = isPreCompetition && competitionData ? (
    <PreCompetitionScreen
      competitionData={competitionData}
      onCompetitionStart={handleCompetitionStart}
      t={t}
    />
  ) : null;

  const shouldRenderHeader = show_react_header && competitionData && competitionData.status !== 'future';

  if (screenMode === 'selection') {
    return (
      <>
        {preCompetitionOverlay}
        <div className="scoreboard-react-layout">
          {shouldRenderHeader && (
            <ScoreboardHeader
              competitionName={competitionData.name}
              competitionDates={competitionData.dates}
              competitionCity={competitionData.city}
            />
          )}
          <ScreenSelection
            onSelectMode={handleScreenModeChange}
            t={t}
          />
        </div>
      </>
    );
  }

  if (screenMode === 'quad') {
    return (
      <>
        {preCompetitionOverlay}
        <div className="scoreboard-react-layout">
          {shouldRenderHeader && (
            <ScoreboardHeader
              competitionName={competitionData.name}
              competitionDates={competitionData.dates}
              competitionCity={competitionData.city}
            />
          )}
          <QuadScreen
            competitionId={competition_id}
            configs={screenConfigs}
            onConfigChange={handleQuadConfigChange}
            onSettingsClick={handleQuadSettingsClick}
            onSwitchScreenMode={handleSwitchScreenMode}
            routes={routes}
            t={t}
          />
        </div>
      </>
    );
  }

  if (screenMode === 'single') {
    return (
      <>
        {preCompetitionOverlay}
        <div className="scoreboard-react-layout">
          {shouldRenderHeader && (
            <ScoreboardHeader
              competitionName={competitionData.name}
              competitionDates={competitionData.dates}
              competitionCity={competitionData.city}
            />
          )}
          <SingleScreen
            competitionId={competition_id}
            config={singleConfig}
            onConfigChange={handleSingleConfigChange}
            onSettingsClick={handleSingleSettingsClick}
            onSwitchScreenMode={handleSwitchScreenMode}
            routes={routes}
            t={t}
          />
        </div>
      </>
    );
  }

  return null;
};

export default ScoreboardShow;
